import React, { useState, useCallback, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { useChat } from '../../../hooks/useChat';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

export default function ContributionOptimizer({ goals, onApplyAllocations }) {
  const { user } = useAuth();
  const [allocation, setAllocation] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [incomeRange, setIncomeRange] = useState('');
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (!user?.id) return;
    supabase?.from('user_profiles')?.select('income_range')?.eq('id', user?.id)?.single()?.then(({ data }) => { if (data?.income_range) setIncomeRange(data?.income_range); });
  }, [user?.id]);

  const optimize = useCallback(() => {
    const activeGoals = goals?.filter(g => g?.goal_status === 'active');
    if (!activeGoals?.length || isLoading) return;
    setHasGenerated(true);
    setIsOpen(true);

    const goalsData = activeGoals?.map(g => {
      const target = parseFloat(g?.target_amount) || 0;
      const current = parseFloat(g?.current_amount) || 0;
      const remaining = target - current;
      const deadline = g?.deadline ? new Date(g.deadline)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'No deadline';
      return `${g?.title}: $${remaining?.toFixed(0)} remaining (target $${target?.toFixed(0)}, deadline: ${deadline})`;
    })?.join('\n');

    const incomeEstimate = incomeRange || 'not specified';

    sendMessage([
      {
        role: 'system',
        content: `You are FinVisAR, a financial planning AI. Analyze financial goals and return ONLY a valid JSON object with these exact fields: "totalMonthly" (recommended total monthly contribution as a number), "disposableEstimate" (estimated disposable income as a number), "rationale" (1-2 sentence explanation of the strategy), "allocations" (array of objects, each with "goalTitle" string, "amount" number, "percentage" number, "priority" string of "High"|"Medium"|"Low", "reasoning" string of 1 sentence). Return ONLY the JSON object.`,
      },
      {
        role: 'user',
        content: `Optimize monthly contribution allocation across my active financial goals:\n\nGoals:\n${goalsData}\n\nIncome range: ${incomeEstimate}\n\nCreate an optimal allocation that balances urgency (deadline proximity), goal size, and financial health. Be specific with dollar amounts.`,
      },
    ], { max_completion_tokens: 800 });
  }, [goals, isLoading, incomeRange, sendMessage]);

  useEffect(() => {
    if (response && hasGenerated && !isLoading) {
      try {
        const match = response?.match(/\{.*\}/s);
        if (match) {
          const parsed = JSON.parse(match?.[0]);
          setAllocation(parsed);
          return;
        }
      } catch {
        // ignore parse errors
      }
    }
  }, [response, hasGenerated, isLoading]);

  const priorityColors = { High: '#EF4444', Medium: '#F59E0B', Low: '#10B981' };

  const activeGoals = goals?.filter(g => g?.goal_status === 'active');

  return (
    <>
      {/* Optimize Button */}
      <button
        onClick={optimize}
        disabled={isLoading || !activeGoals?.length}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50 hover:opacity-90"
        style={{ background: 'linear-gradient(135deg, rgba(123,47,190,0.3), rgba(0,229,255,0.2))', border: '1px solid rgba(123,47,190,0.4)', color: '#A78BFA' }}
      >
        {isLoading ? (
          <><div className="w-4 h-4 border-2 border-[#A78BFA] border-t-transparent rounded-full animate-spin" />Optimizing...</>
        ) : (
          <><Icon name="Sparkles" size={16} color="#A78BFA" />Optimize My Goals</>
        )}
      </button>
      {/* Optimizer Panel */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div
            className="w-full max-w-lg rounded-2xl overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1a0a2e 100%)', border: '1px solid rgba(123,47,190,0.3)', boxShadow: '0 25px 50px rgba(0,0,0,0.5)' }}
          >
            {/* Modal Header */}
            <div
              className="px-6 py-4 flex items-center justify-between"
              style={{ background: 'linear-gradient(135deg, rgba(123,47,190,0.2), rgba(0,229,255,0.1))', borderBottom: '1px solid rgba(123,47,190,0.2)' }}
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #7B2FBE, #00E5FF)' }}>
                  <Icon name="Sparkles" size={16} color="#fff" />
                </div>
                <div>
                  <h3 className="text-[#F8FAFC] font-bold text-sm">Contribution Optimizer</h3>
                  <p className="text-[#64748B] text-xs">AI-recommended monthly allocation</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[rgba(255,255,255,0.08)] transition-colors">
                <Icon name="X" size={16} color="#64748B" />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Loading */}
              {isLoading && (
                <div className="space-y-4 animate-pulse">
                  <div className="h-4 bg-[rgba(255,255,255,0.06)] rounded w-2/3" />
                  <div className="h-3 bg-[rgba(255,255,255,0.04)] rounded w-full" />
                  {[1, 2, 3]?.map(i => (
                    <div key={i} className="h-16 bg-[rgba(255,255,255,0.04)] rounded-xl" />
                  ))}
                  <p className="text-center text-xs text-[#64748B]">AI is calculating optimal allocations...</p>
                </div>
              )}

              {/* Error */}
              {!isLoading && error && (
                <div className="text-center py-6">
                  <Icon name="AlertCircle" size={28} color="#EF4444" />
                  <p className="text-[#EF4444] text-sm mt-2">Unable to optimize. Please try again.</p>
                </div>
              )}

              {/* Allocation Results */}
              {!isLoading && allocation && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.06), rgba(123,47,190,0.06))', border: '1px solid rgba(0,229,255,0.15)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-[#64748B]">Recommended Monthly Total</span>
                      <span className="text-lg font-bold font-mono text-[#00E5FF]">${allocation?.totalMonthly?.toLocaleString()}</span>
                    </div>
                    <p className="text-[#94A3B8] text-xs leading-relaxed">{allocation?.rationale}</p>
                  </div>

                  {/* Allocations */}
                  <div className="space-y-2">
                    {allocation?.allocations?.map((alloc, idx) => (
                      <div key={idx} className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                              style={{ color: priorityColors?.[alloc?.priority] || '#F59E0B', backgroundColor: `${priorityColors?.[alloc?.priority] || '#F59E0B'}15` }}
                            >
                              {alloc?.priority}
                            </span>
                            <span className="text-[#E2E8F0] text-sm font-medium">{alloc?.goalTitle}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-[#00E5FF] font-bold font-mono text-sm">${alloc?.amount?.toLocaleString()}</span>
                            <span className="text-[#475569] text-xs">/mo</span>
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div className="h-1.5 bg-[#1E293B] rounded-full overflow-hidden mb-1.5">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${alloc?.percentage || 0}%`, background: 'linear-gradient(90deg, #7B2FBE, #00E5FF)' }}
                          />
                        </div>
                        <p className="text-[#64748B] text-[11px]">{alloc?.reasoning}</p>
                      </div>
                    ))}
                  </div>

                  {/* Apply Button */}
                  <button
                    onClick={() => {
                      onApplyAllocations?.(allocation?.allocations);
                      setIsOpen(false);
                    }}
                    className="w-full py-3 rounded-xl text-sm font-bold text-[#0F172A] transition-all duration-200 hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)' }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Icon name="Check" size={16} color="#0F172A" />
                      Apply Allocations
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
